/** Company receipt reads share the existing agreement Query/authority lifecycle. */
import { useLayoutEffect, useRef } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { companySoftwareBillingApi as api } from "@bw/shared/api";
import type { CompanySoftwareBillingScope, CompanyPurchaseId, CompanyReceiptView,
  CompanyReceiptPage, CompanyReceiptOriginal, CompanyReceiptPageRequest } from "@bw/shared/api";
import type { CompanySoftwareReceiptHooks } from "./companyReceiptContracts";
import { checkAgreement, exactKeys, uuid, useScopedQuery, companyBillingQueryKey,
  CompanyBillingRequestError } from "./useModuleBilling";

const recordKeys = ["org_id", "environment", "purchase_id", "acceptance_id", "receipt_id", "invoice_id",
  "kind", "cash_state", "collected_amount", "recorded_at", "merchant_name", "purpose", "confirmation_number"];
const awareTime = (value: unknown): boolean => typeof value === "string"
  && /^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/i.test(value) && Number.isFinite(Date.parse(value));
const snapshot = (value: unknown): boolean => value === null || typeof value === "string" && value.length >= 1 && value.length <= 160;

/** Same UUID-byte Base32 display code as common.utils, without padding.
 * Pure correlation only: it cannot issue cash, authority or a receipt.
 */
function confirmationNumber(identifier: string): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567", hex = identifier.replaceAll("-", "");
  let buffer = 0, bits = 0, result = "BW-";
  for (let offset = 0; offset < hex.length; offset += 2) {
    buffer = (buffer << 8) | Number.parseInt(hex.slice(offset, offset + 2), 16);
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      result += alphabet[(buffer >>> bits) & 31];
    }
    buffer &= (1 << bits) - 1;
  }
  if (bits > 0) result += alphabet[buffer << (5 - bits)];
  return result;
}

function record(value: CompanyReceiptOriginal, scope: CompanySoftwareBillingScope, purchaseId: CompanyPurchaseId): void {
  exactKeys(value, recordKeys); checkAgreement(value, scope);
  exactKeys(value.collected_amount, ["amount_minor", "currency"]);
  if (![value.org_id, value.acceptance_id, value.purchase_id, value.receipt_id, value.invoice_id].every(uuid)
    || value.purchase_id !== purchaseId || value.kind !== "charge" || value.cash_state !== "recorded"
    || !Number.isSafeInteger(value.collected_amount.amount_minor) || value.collected_amount.amount_minor <= 0
    || !["aud", "cad", "eur", "usd"].includes(value.collected_amount.currency) || !awareTime(value.recorded_at)
    || !snapshot(value.merchant_name) || !snapshot(value.purpose)
    || typeof value.confirmation_number !== "string" || value.confirmation_number !== confirmationNumber(value.receipt_id))
    throw new Error("Invalid company recorded cash");
}

export function receiptOriginal(value: CompanyReceiptView): CompanyReceiptOriginal {
  const { invoice_state, ...original } = value;
  return original;
}

/** Mutable invoice state is deliberately absent from immutable cash comparison. */
export function sameReceiptOriginal(left: CompanyReceiptOriginal, right: CompanyReceiptOriginal): boolean {
  return left.org_id === right.org_id && left.environment === right.environment && left.acceptance_id === right.acceptance_id
    && left.purchase_id === right.purchase_id && left.receipt_id === right.receipt_id && left.invoice_id === right.invoice_id
    && left.kind === right.kind && left.cash_state === right.cash_state && left.recorded_at === right.recorded_at
    && left.collected_amount.amount_minor === right.collected_amount.amount_minor && left.collected_amount.currency === right.collected_amount.currency
    && left.merchant_name === right.merchant_name && left.purpose === right.purpose && left.confirmation_number === right.confirmation_number;
}

export function checkCompanyReceipt(value: CompanyReceiptView, scope: CompanySoftwareBillingScope,
  purchaseId: CompanyPurchaseId): void {
  exactKeys(value, [...recordKeys, "invoice_state"]);
  record(receiptOriginal(value), scope, purchaseId);
  if (!["verified_paid", "pending", "uncertain", "failed", "refunded"].includes(value.invoice_state))
    throw new Error("Invalid company receipt invoice evidence");
}

function requestPage(page: CompanyReceiptPageRequest): void {
  exactKeys(page, ["limit", "offset"]);
  if (!Number.isSafeInteger(page.limit) || page.limit < 1 || page.limit > 100
    || !Number.isSafeInteger(page.offset) || page.offset < 0) throw new Error("Invalid company receipt page request");
}

export function checkCompanyReceiptPage(value: CompanyReceiptPage, scope: CompanySoftwareBillingScope,
  purchaseId: CompanyPurchaseId, page: CompanyReceiptPageRequest): void {
  requestPage(page);
  exactKeys(value, ["org_id", "environment", "purchase_id", "acceptance_id", "items", "total", "limit", "offset"]);
  checkAgreement(value, scope);
  if (![value.org_id, value.acceptance_id, value.purchase_id].every(uuid) || value.purchase_id !== purchaseId
    || !Array.isArray(value.items) || !Number.isSafeInteger(value.total) || value.total < 0
    || value.limit !== page.limit || value.offset !== page.offset || value.items.length > page.limit
    || value.items.length > Math.max(0, value.total - page.offset)
    || new Set(value.items.map(item => item.receipt_id)).size !== value.items.length
    || new Set(value.items.map(item => item.invoice_id)).size !== value.items.length)
    throw new Error("Invalid company receipt page");
  value.items.forEach(item => checkCompanyReceipt(item, scope, purchaseId));
}

function knownReceipt(client: QueryClient, scope: CompanySoftwareBillingScope, purchaseId: CompanyPurchaseId,
  original: CompanyReceiptOriginal): void {
  for (const [, page] of client.getQueriesData<CompanyReceiptPage>({ queryKey: [...companyBillingQueryKey(scope), "listReceipts", purchaseId] })) {
    if (!page) continue;
    checkCompanyReceiptPage(page, scope, purchaseId, { limit: page.limit, offset: page.offset });
    const prior = page.items.find(item => item.receipt_id === original.receipt_id || item.invoice_id === original.invoice_id);
    if (prior && !sameReceiptOriginal(receiptOriginal(prior), original)) throw new Error("Canonical receipt record changed");
  }
}

/** Selection changes invalidate this request, never the whole agreement's
 * Query namespace. Both hooks stay mounted when selection/detail is null.
 */
function useSelectionLease(scope: CompanySoftwareBillingScope, purchaseId: CompanyPurchaseId | null,
  original: CompanyReceiptOriginal | null): () => void {
  const live = useRef({ purchaseId, original, active: true });
  live.current.purchaseId = purchaseId; live.current.original = original;
  useLayoutEffect(() => { live.current.active = true; return () => { live.current.active = false; }; }, []);
  return () => {
    if (!live.current.active || purchaseId === null || !uuid(purchaseId) || live.current.purchaseId !== purchaseId
      || (original === null ? live.current.original !== null : live.current.original === null || !sameReceiptOriginal(original, live.current.original)))
      throw new CompanyBillingRequestError(scope, original === null ? "listReceipts" : "getReceipt", 503, "COMPANY_SELECTION_CHANGED");
  };
}

export function useCompanyReceipts(scope: CompanySoftwareBillingScope, billingFenceRevision: number,
  suspendAutomaticReads: boolean, purchaseId: CompanyPurchaseId | null, page: CompanyReceiptPageRequest,
): ReturnType<CompanySoftwareReceiptHooks["useCompanyReceipts"]> {
  const client = useQueryClient(), selected = useSelectionLease(scope, purchaseId, null);
  return useScopedQuery(scope, billingFenceRevision, "listReceipts", [purchaseId, page.limit, page.offset],
    !suspendAutomaticReads && purchaseId !== null, async () => {
      selected(); requestPage(page);
      if (purchaseId === null) throw new Error("No original records selected");
      const value = await api.listReceipts(scope.orgId, purchaseId, scope.environment, page);
      selected(); checkCompanyReceiptPage(value, scope, purchaseId, page);
      value.items.forEach(item => knownReceipt(client, scope, purchaseId, receiptOriginal(item))); return value;
    });
}

export function useCompanyReceipt(scope: CompanySoftwareBillingScope, billingFenceRevision: number,
  suspendAutomaticReads: boolean, purchaseId: CompanyPurchaseId | null, original: CompanyReceiptOriginal | null,
): ReturnType<CompanySoftwareReceiptHooks["useCompanyReceipt"]> {
  const client = useQueryClient(), selected = useSelectionLease(scope, purchaseId, original);
  return useScopedQuery(scope, billingFenceRevision, "getReceipt", [purchaseId, original?.receipt_id ?? null],
    !suspendAutomaticReads && purchaseId !== null && original !== null, async () => {
      selected();
      if (purchaseId === null || original === null) throw new Error("No original receipt selected");
      record(original, scope, purchaseId);
      knownReceipt(client, scope, purchaseId, original);
      const value = await api.getReceipt(scope.orgId, purchaseId, original.receipt_id, scope.environment);
      selected(); checkCompanyReceipt(value, scope, purchaseId); knownReceipt(client, scope, purchaseId, original);
      if (!sameReceiptOriginal(receiptOriginal(value), original)) throw new Error("Foreign company receipt record");
      return value;
    });
}
